import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IoChevronDown } from 'react-icons/io5';

const Section = styled.section`
  padding: 5rem 2rem;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: ${({ theme }) => theme.colors.activeColor}20;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  border-radius: 50px;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.headline};

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AccordionItem = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: ${({ theme }) => theme.radii.sm};
  overflow: hidden;
  transition: border-color 0.2s;

  ${({ $open, theme }) => $open && `
    border-color: ${theme.colors.activeColor}40;
  `}
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: ${({ theme }) => theme.colors.cardBG || theme.colors.bg};
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.headline};
  font-family: inherit;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const ChevronWrapper = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.activeColor};
  transition: transform 0.3s ease;
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  flex-shrink: 0;
  margin-left: 1rem;
`;

const AccordionBody = styled.div`
  max-height: ${({ $open }) => $open ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const AccordionContent = styled.div`
  padding: 0.625rem 1.5rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.paragraph};
  line-height: 1.7;
  background: ${({ theme }) => theme.colors.cardBG};
  border-radius: 0 0 ${({ theme }) => theme.radii.sm} ${({ theme }) => theme.radii.sm};
`;

const faqs = [
  {
    question: 'What makes PropNinja different from a spreadsheet?',
    answer: 'PropNinja pulls live MLS listings, estimates rent using up-to-date market data, and calculates CapEx reserves based on each component\u2019s age\u2014not flat percentages. You get investor-grade analysis without manual data entry.'
  },
  {
    question: 'How accurate are the rent estimates?',
    answer: 'We pull up-to-date market data from leading providers. Estimates factor in bed/bath count, square footage, property condition, and neighborhood\u2014not national averages.'
  },
  {
    question: 'What\u2019s included in the CapEx calculation?',
    answer: 'Reserves for roof, HVAC, water heater, appliances, flooring, plumbing, electrical, windows, siding, driveway, cabinets, landscaping, and more. Each uses estimated age, useful life, and local replacement cost based on your contractor level. Use global age settings for quick browsing, or fine-tune per item for complete accuracy\u2014your choice. CapEx is optional and fully customizable.'
  },
  {
    question: 'Can I try it before paying?',
    answer: 'Yes. The free tier gives you 5 analyses per month with no credit card required. It never expires and resets every 30 days.'
  },
  {
    question: 'What metrics do I get?',
    answer: 'Cash-on-cash return, cap rate, monthly cash flow, IRR projections, amortization schedules, and valuation at sale\u2014all calculated over your chosen time horizon, whether that\u2019s 5 years or 30.'
  }
];

const FAQ = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const fallback = setTimeout(() => setIsVisible(true), 3500);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <Section ref={sectionRef}>
      <Container>
        <SectionHeader className={isVisible ? 'visible' : ''}>
          <SectionLabel>FAQ</SectionLabel>
          <SectionTitle>
            Common Questions
          </SectionTitle>
        </SectionHeader>
        <AccordionList className={isVisible ? 'visible' : ''}>
          {faqs.map((faq, index) => (
            <AccordionItem key={index} $open={openIndex === index}>
              <AccordionHeader onClick={() => toggle(index)}>
                {faq.question}
                <ChevronWrapper $open={openIndex === index}>
                  <IoChevronDown size={18} />
                </ChevronWrapper>
              </AccordionHeader>
              <AccordionBody $open={openIndex === index}>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionBody>
            </AccordionItem>
          ))}
        </AccordionList>
      </Container>
    </Section>
  );
};

export default FAQ;
